import { ChangeDetectionStrategy, Component, computed, DestroyRef, EventEmitter, inject, OnInit, Output, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MineButtonDirective } from '@mineos/mine-ui';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Animations } from 'src/app/animations/animations';
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { ContentPipeModule } from 'src/app/services/content/content-pipe.module';
import { DropdownOption } from 'src/app/shared/mine-dropdown/mine-dropdown.interface';
import { MineActionMenuComponent } from 'src/app/shared/mine-action-menu/mine-action-menu.component';
import { MineSpinnerComponent } from 'src/app/shared/mine-spinner/mine-spinner.component';
import { SkeletonDirective } from 'src/app/shared/skeletons/skeleton.directive';
import { NotificationsService } from '../services/notifications.service';
import { NotificationsQuery } from '../state/notifications.query';
import { NotificationItemComponent } from './notification-item/notification-item.component';
import { NotificationInboxDto, NotificationTypeEnum } from '../models/notification.interface';
import { Router } from '@angular/router';
import { LoggerService } from 'src/app/logger/logger.service';
import { RoutesManager } from 'src/app/shared/models/routes.interfaces';
import { AssessmentsSplitByUseCaseService } from 'src/app/ai-assessments/services/assessments-split-by-use-case.service';
import { AssessmentsService } from 'src/app/ai-assessments/services/assessments.service';
import { AssessmentTemplateEnum } from 'src/app/api/models/assessments/assessment.enum';
import { NotificationAssessmentIntentService } from '../services/notification-assessment-intent.service';

interface NotificationGroup {
    label: string;
    items: NotificationInboxDto[];
}

interface NotificationCommentQueryParams {
    openCommentsPanel: '1';
    sectionId?: string;
    assessmentQuestionId?: string;
    notesThreadId?: string;
}

@Component({
    standalone: true,
    selector: 'notifications-panel',
    templateUrl: './notifications-panel.component.html',
    styleUrls: ['./notifications-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ScrollingModule, ContentPipeModule, MineActionMenuComponent, NotificationItemComponent, SkeletonDirective, MineSpinnerComponent, MineButtonDirective],
    animations: [Animations.pimple],
})
export class NotificationsPanelComponent implements OnInit {

    private readonly router = inject(Router);

    private readonly logger = inject(LoggerService);

    @Output() closePanel = new EventEmitter<void>();

    readonly panelLoading = signal(true);

    readonly emptyStateImageUrl = `${environment.assets.url}/Portal/assessment-completed.png`;

    loading: Signal<boolean>;
    isLoadingMore: Signal<boolean>;

    readonly filterMenuOptions = computed<DropdownOption[]>(() => [
        { id: 'showAll', value: this.contentPipe.transform('in-app-notifications.showAll'), icon: !this.notificationsQuery.filterUnread() ? 'Check' : ' ', iconColor: 'var(--mine-purple-dark)' },
        { id: 'unreadOnly', value: this.contentPipe.transform('in-app-notifications.filterUnreadLabel'), icon: this.notificationsQuery.filterUnread() ? 'Check' : ' ', iconColor: 'var(--mine-purple-dark)' },
    ]);

    readonly actionMenuOptions: DropdownOption[] = [
        { id: 'markAllAsRead', value: this.contentPipe.transform('in-app-notifications.markAllAsRead') },
    ];

    readonly hasActiveFilters = computed(() => this.notificationsQuery.filterUnread());

    readonly groupedItems = computed<NotificationGroup[]>(() => {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        const sevenDaysAgo = new Date(todayStart);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        const items = this.notificationsQuery.items();

        const todayItems = items.filter(item => new Date(item.createdAt) >= todayStart);
        const yesterdayItems = items.filter(item => {
            const d = new Date(item.createdAt);
            return d >= yesterdayStart && d < todayStart;
        });
        const lastSevenDaysItems = items.filter(item => {
            const d = new Date(item.createdAt);
            return d >= sevenDaysAgo && d < yesterdayStart;
        });
        const olderItems = items.filter(item => new Date(item.createdAt) < sevenDaysAgo);

        const groups: NotificationGroup[] = [];
        if (todayItems.length) groups.push({ label: 'in-app-notifications.groupToday', items: todayItems });
        if (yesterdayItems.length) groups.push({ label: 'in-app-notifications.groupYesterday', items: yesterdayItems });
        if (lastSevenDaysItems.length) groups.push({ label: 'in-app-notifications.groupLastSevenDays', items: lastSevenDaysItems });
        if (olderItems.length) groups.push({ label: 'in-app-notifications.groupOlder', items: olderItems });
        return groups;
    });

    readonly isFilterEmpty = computed(() =>
        this.notificationsQuery.filterUnread() &&
        this.groupedItems().length === 0 &&
        !this.notificationsQuery.loading()
    );

    readonly isGeneralEmpty = computed(() =>
        !this.notificationsQuery.filterUnread() &&
        this.groupedItems().length === 0 &&
        !this.notificationsQuery.loading()
    );

    /** Avoid showing pagination while skeleton or empty-state banners are visible. */
    readonly showLoadMore = computed(
        () =>
            !this.notificationsQuery.loading() &&
            !this.panelLoading() &&
            !this.isFilterEmpty() &&
            !this.isGeneralEmpty() &&
            this.notificationsQuery.hasMore(),
    );

    constructor(
        private notificationsService: NotificationsService,
        private notificationsQuery: NotificationsQuery,
        private contentPipe: ContentPipe,
        private destroyRef: DestroyRef,
        private assessmentsSplitByUseCaseService: AssessmentsSplitByUseCaseService,
        private assessmentsService: AssessmentsService,
        private notificationAssessmentIntent: NotificationAssessmentIntentService,
    ) {}

    ngOnInit(): void {
        this.notificationsService.refresh();
        this.loading = this.notificationsQuery.loading;
        this.isLoadingMore = this.notificationsQuery.loadingMore;
        timer(0).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.panelLoading.set(false));
    }

    onFilterMenuAction(id: string): void {
        if (id === 'showAll' && this.notificationsQuery.filterUnread()) {
            this.notificationsService.toggleFilter();
        } else if (id === 'unreadOnly' && !this.notificationsQuery.filterUnread()) {
            this.notificationsService.toggleFilter();
        }
    }

    onActionMenuClick(id: string): void {
        if (id === 'markAllAsRead') {
            this.notificationsService.markAllAsRead();
        }
    }

    onItemClick(notification: NotificationInboxDto): void {
        this.notificationsService.onNotificationClick(notification);
        this.closePanel.emit();
        this.navigateToNotification(notification);
    }

    private navigateToNotification(notification: NotificationInboxDto): void {
        switch (notification.notificationType) {
            case NotificationTypeEnum.AssessmentAssignOwner:
                this.navigateToAssessment(notification.metadata?.assessmentId);
                break;
            case NotificationTypeEnum.AssessmentCollabSubmitted:
                this.navigateToAssessment(notification.metadata?.assessmentId);
                break;
            case NotificationTypeEnum.AssessmentApproved:
                this.navigateToAssessment(notification.metadata?.assessmentId);
                break;
            case NotificationTypeEnum.AutofillSuggestionsReady:
                // Autofill runs on either a system (vendor scout) or an assessment; route by
                // whichever entity id the metadata carries.
                if (notification.metadata?.systemId) {
                    this.router.navigate([RoutesManager.systems, notification.metadata.systemId]);
                } else if (notification.metadata?.assessmentId) {
                    this.navigateToAssessment(notification.metadata.assessmentId);
                }
                break;
            case NotificationTypeEnum.AssessmentNoteAdded:
                this.navigateToAssessment(notification.metadata?.assessmentId, {
                    openCommentsPanel: true,
                    sectionId: notification.metadata?.sectionId,
                    assessmentQuestionId: notification.metadata?.assessmentQuestionId,
                    notesThreadId: notification.metadata?.notesThreadId,
                });
                break;
            case NotificationTypeEnum.SystemAssignOwner:
                this.router.navigate([RoutesManager.systems, notification.metadata?.systemId]);
                break;
            case NotificationTypeEnum.SystemApproved:
                this.router.navigate([RoutesManager.systems, notification.metadata?.systemId]);
                break;
            case NotificationTypeEnum.AiTemplateReady:
                // "Create a template with AI" saved the template; open it in the builder. Failed
                // variants carry no templateId, so they stay non-navigable (default below).
                if (notification.metadata?.templateId) {
                    this.router.navigate([RoutesManager.assessments, RoutesManager.assessments_templates, notification.metadata.templateId]);
                }
                break;
            default:
                break;
        }
    }

    private assessmentCommands(assessmentId: string | undefined): [string, string] | null {
        if (!assessmentId) {
            return null;
        }
        const cached = this.assessmentsService.getAssessmentById(assessmentId);
        const templateType = cached?.type as AssessmentTemplateEnum | undefined;
        if (templateType) {
            const useCase = this.assessmentsSplitByUseCaseService.selectUseCaseByTemplateType(templateType);
            const base = this.assessmentsSplitByUseCaseService.selectAssessmentsRoute(useCase);
            return [base, assessmentId];
        }
        return [RoutesManager.assessments, assessmentId];
    }

    private navigateToAssessment(
        assessmentId: string | undefined,
        commentState?: {
            openCommentsPanel: boolean;
            sectionId?: string;
            assessmentQuestionId?: string;
            notesThreadId?: string;
        },
    ): void {
        const commands = this.assessmentCommands(assessmentId);
        if (!commands) {
            return;
        }
        const extras: {
            onSameUrlNavigation: 'reload';
            queryParams?: NotificationCommentQueryParams;
        } = {
            onSameUrlNavigation: 'reload',
        };
        if (commentState) {
            extras.queryParams = this.buildNotificationCommentQueryParams(commentState);
        }
        void this.router.navigate(commands, extras).then(success => {
            if (
                !success &&
                commentState?.openCommentsPanel &&
                assessmentId
            ) {
                this.notificationAssessmentIntent.emitOpenAssessmentFromNotification({
                    assessmentId,
                    sectionId: commentState.sectionId,
                    assessmentQuestionId: commentState.assessmentQuestionId,
                    notesThreadId: commentState.notesThreadId,
                });
            }
        });
    }

    private buildNotificationCommentQueryParams(commentState: {
        openCommentsPanel: boolean;
        sectionId?: string;
        assessmentQuestionId?: string;
        notesThreadId?: string;
    }): NotificationCommentQueryParams {
        return {
            openCommentsPanel: '1',
            ...(commentState.sectionId ? { sectionId: commentState.sectionId } : {}),
            ...(commentState.assessmentQuestionId ? { assessmentQuestionId: commentState.assessmentQuestionId } : {}),
            ...(commentState.notesThreadId ? { notesThreadId: commentState.notesThreadId } : {}),
        };
    }

    onMarkRead(id: string): void {
        this.notificationsService.markAsRead(id);
    }

    onMarkUnread(id: string): void {
        this.notificationsService.markAsUnread(id);
    }

    onDeleteItem(id: string): void {
        this.notificationsService.deleteNotification(id);
    }

    onLoadMore(): void {
        this.notificationsService.loadMore();
    }

    trackByGroup(_index: number, group: NotificationGroup): string {
        return group.label;
    }

    trackByItem(_index: number, item: NotificationInboxDto): string {
        return item.id;
    }
}
